import type { Filter } from "#shared/schemas/filter.ts";
import { BoundedFilterSchema } from "#shared/schemas/filter.ts";

/** Comma-separated URL lists; single ids from older links still parse. */
export function parseIdList(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return [...new Set(raw.split(",").map((s) => s.trim()).filter(Boolean))];
}

export function formatIdList(ids: readonly string[]): string | null {
  return ids.length > 0 ? ids.join(",") : null;
}

function idLeaf(field: string, ids: readonly string[]): Filter | null {
  if (ids.length === 0) return null;
  if (ids.length === 1) return { field, cmp: "eq", value: ids[0]! };
  return { field, cmp: "in", value: [...ids] };
}

/**
 * Build the API `filter` JSON from URL facets + optional opaque `?filter=`
 * (graph / explore). Facets AND with the opaque filter when both set.
 * Search `q` stays a separate query param — do not bake it in here.
 */
export function buildBrowseFilter(input: {
  artistIds: readonly string[];
  albumIds: readonly string[];
  genreIds: readonly string[];
  rawFilter: string | null;
}): string | null {
  const nodes: Filter[] = [];

  if (input.rawFilter) {
    try {
      const parsed: unknown = JSON.parse(input.rawFilter);
      const result = BoundedFilterSchema.safeParse(parsed);
      if (result.success) nodes.push(result.data);
    } catch {
      // ignore malformed
    }
  }

  const artist = idLeaf("artist.id", input.artistIds);
  const album = idLeaf("album.id", input.albumIds);
  const genre = idLeaf("genre.id", input.genreIds);
  if (artist) nodes.push(artist);
  if (album) nodes.push(album);
  if (genre) nodes.push(genre);

  if (nodes.length === 0) return null;
  const filter: Filter = nodes.length === 1 ? nodes[0]! : { op: "and", nodes };
  return JSON.stringify(filter);
}

/** Filter AST for saving a view (includes `q` as track.name contains). */
export function browseFilterAst(input: {
  artistIds: readonly string[];
  albumIds: readonly string[];
  genreIds: readonly string[];
  rawFilter: string | null;
  q?: string | null;
}): Filter | null {
  const nodes: Filter[] = [];
  const baseJson = buildBrowseFilter(input);
  if (baseJson) {
    const parsed = BoundedFilterSchema.safeParse(JSON.parse(baseJson) as unknown);
    if (parsed.success) {
      if ("op" in parsed.data && parsed.data.op === "and") {
        nodes.push(...parsed.data.nodes);
      } else {
        nodes.push(parsed.data);
      }
    }
  }
  const q = input.q?.trim();
  if (q) nodes.push({ field: "track.name", cmp: "contains", value: q });
  if (nodes.length === 0) return null;
  return nodes.length === 1 ? nodes[0]! : { op: "and", nodes };
}

/** Expand selected genre ids to include all descendants (hierarchy-inclusive). */
export function expandGenreSubtree(
  selectedIds: readonly string[],
  genres: readonly { id: string; parent_id: string | null }[],
): Set<string> {
  const children = new Map<string | null, string[]>();
  for (const g of genres) {
    const list = children.get(g.parent_id) ?? [];
    list.push(g.id);
    children.set(g.parent_id, list);
  }
  const out = new Set<string>();
  const stack = [...selectedIds];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const child of children.get(id) ?? []) stack.push(child);
  }
  return out;
}
